<template>
  <div>
    <label :for="id" class="form-label">
      {{ label }}
      <span v-if="validate.includes('required') && !disabled" class="required-status">*</span>
    </label>

    <div v-if="!disabled" class="canvas-container">
      <button
        v-if="uploadStatus"
        :class="{ disabled: loading }"
        class="btn btn-success d-flex gap-2"
        @click.prevent="uploadTheFile"
      >
        <LoadingSpinner v-if="loading" />
        <i v-else class="mdi mdi-upload"></i>
        {{ $t('main.save') }}
      </button>
      <canvas
        ref="canvas"
        width="800"
        height="600"
        @mousedown="startDrawing"
        @mousemove="draw"
        @mouseup="stopDrawing"
        @mouseleave="stopDrawing"
      ></canvas>

      <div
        v-if="(Array.isArray(errors) && errors.length) || errors.length || meta?.errors?.length"
        class="invalid-feedback"
      >
        {{ Array.isArray(errors) ? errors[0] : errors }}
        {{ Array.isArray(meta.errors) ? meta.errors[0] : '' }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import { customAlert } from '@/utils/sweetalert';
import { getFileURL } from '@/utils/helper';
import { commonUploadFile, generatePresignedUrl } from '@/services/main';

const { t } = useI18n();
const emit = defineEmits(['change']);

const loading = ref(false);

const canvas = ref(null);
const image = ref(null);
const isDrawing = ref(false);
const startX = ref(0);
const startY = ref(0);
const maxCanvasWidth = 800;

const props = defineProps({
  id: { type: String, default: '' },
  label: { type: String, default: '' },
  validate: { type: [String, Object], default: '' },
  errors: { type: [String, Array], default: '' },
  url: { type: String, default: '' },

  uploadStatus: { type: Boolean, default: false },
  uploadUrl: { type: String, default: '' },
  uploadFilePath: { type: String, default: '' },
});

const loadImageFromUrl = () => {
  const imageUrl = getFileURL(props.url) || '';

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const ctx = canvas.value.getContext('2d');

    // Resize image to fit within the canvas (max width: 800)
    let width = img.width;
    let height = img.height;

    // Resize only if the image width is larger than maxCanvasWidth
    if (width > maxCanvasWidth) {
      const aspectRatio = height / width;
      width = maxCanvasWidth;
      height = width * aspectRatio;
    } else if (width < maxCanvasWidth) {
      const aspectRatio = height / width;
      width = maxCanvasWidth;
      height = width * aspectRatio;
    }

    // Set the canvas size based on the resized image or original dimensions
    canvas.value.width = width;
    canvas.value.height = height;

    // Draw the image on the canvas (resized or original)
    ctx.drawImage(img, 0, 0, width, height);
    image.value = img;
  };
  img.src = imageUrl; // Set the image source from the URL

  setTimeout(() => {
    uploadTheFile(false);
  }, 500);
};
const getMousePos = (event) => {
  const rect = canvas.value.getBoundingClientRect(); // Get canvas position
  return {
    x: event.clientX - rect.left, // Mouse X relative to canvas
    y: event.clientY - rect.top, // Mouse Y relative to canvas
  };
};
const startDrawing = (event) => {
  isDrawing.value = true;
  const pos = getMousePos(event);
  startX.value = pos.x;
  startY.value = pos.y;
};
const draw = (event) => {
  if (!isDrawing.value) return;

  const pos = getMousePos(event);

  const ctx = canvas.value.getContext('2d');
  ctx.strokeStyle = 'blue'; // Line color
  ctx.lineWidth = 3; // Line width
  ctx.lineCap = 'round'; // Smooth line edges

  ctx.beginPath();
  ctx.moveTo(startX.value, startY.value);
  ctx.lineTo(pos.x, pos.y);
  ctx.stroke();
  ctx.closePath();

  startX.value = pos.x;
  startY.value = pos.y;
};
const stopDrawing = () => {
  isDrawing.value = false;
  // uploadTheFile();
};

const uploadTheFile = (showSuccessAlert = true) => {
  canvas.value.toBlob((blob) => {
    // Emit the Blob file to the parent component
    const file = new File([blob], 'canvas-image.png', { type: 'image/png' });
    if (props.uploadStatus) {
      loading.value = true;
      const formData = new FormData();
      formData.append('filename', file?.name);
      formData.append('path', props.uploadFilePath);
      generatePresignedUrl(props.uploadUrl, formData)
        .then((res) => {
          const { uploading_url, headers } = res.data;
          commonUploadFile(uploading_url, file, headers)
            .then(() => {
              emit('change', res.data.file_url || '');
              if (showSuccessAlert) customAlert(t('main.done'), 'success');
            })
            .catch(() => {
              emit('change', '');
            })
            .finally(() => {
              loading.value = false;
            });
        })
        .catch(() => {
          emit('change', '');
          loading.value = false;
        });
    } else {
      emit('change', file);
    }
  }, 'image/png');
};

if (props.url) {
  loadImageFromUrl();
}
</script>

<style scoped lang="scss">
.canvas-container {
  width: 800px;
  // margin: 0 auto;
  border: 1px solid #ddd;
}
</style>
